/**
 * Pré-carga DocumentReference via STATISTICS batch.
 * MedicationDispense vai em um SEGUNDO batch (não mistura com docs) para
 * o servidor falhar o dispense sem derrubar a pré-carga documental.
 * @module GetEcommercePatientResources
 */

import type { Bundle, DocumentReference, MedicationDispense, Patient } from "fhir/r5";
import { endpoints, oids } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";
import { USE_ECOMMERCE_RESOURCES_BATCH_DISPENSE } from "src-next/modules/patient-supporting-documents/purchase-release/constants";

/** Categories de documentos de suporte do ecommerce. */
export const ECOMMERCE_SUPPORT_DOC_CATEGORIES = [
  "anvisa_import_authorization",
  "medical_prescription",
  "personal_document",
  "proof_of_residence",
] as const;

const BATCH_CHUNK_SIZE = 50;
const TIMEOUT_MS = 60000;

const DOC_ELEMENTS =
  "id,subject,category,status,docStatus,attester,date,description,extension,meta";
const DISPENSE_ELEMENTS =
  "id,status,subject,medication,quantity,whenPrepared,recorded,performer,extension,meta";

export interface EcommercePatientResourcesResult {
  documentsByPatient: Map<string, DocumentReference[]>;
  dispensesByPatient: Map<string, MedicationDispense[]>;
  /** false se nenhum chunk do batch de documentos teve sucesso. */
  ok: boolean;
  /** true se pelo menos um chunk do batch de MedicationDispense teve sucesso. */
  dispensesOk: boolean;
}

function patientKeyFromReference(reference?: string): string {
  if (!reference) return "";
  const trimmed = reference.trim();
  if (trimmed.startsWith("Patient/")) return trimmed;
  return `Patient/${trimmed}`;
}

function subjectKeyFromPatient(patient: Patient): string {
  if (patient.id) return `Patient/${patient.id}`;
  return "";
}

function matchesOrganizationPerformer(
  dispense: MedicationDispense,
  organizationId: string,
): boolean {
  const expected = `Organization/${oids.cnpj}-${organizationId}`;
  return Boolean(
    dispense.performer?.some((entry) => {
      const ref = entry.actor?.reference?.trim() || "";
      return (
        ref === expected ||
        ref.endsWith(`-${organizationId}`) ||
        ref.includes(organizationId)
      );
    }),
  );
}

async function statisticsHeaders(): Promise<Record<string, string>> {
  const subjectId = await useAuth.getSubjectId();
  const orgIdHeader = await useAuth.getOrganizationId();
  const authorization = (await useAuth.getUserToken()) ?? "";
  return {
    Accept: "application/fhir+json",
    Authorization: authorization,
    "Content-Type": "application/fhir+json",
    ...(subjectId && { "subject-id": subjectId }),
    ...(orgIdHeader && { "organization-id": orgIdHeader }),
  };
}

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function buildDocsBatchBundle(patientRefs: string[]): Bundle {
  const categoryParam = ECOMMERCE_SUPPORT_DOC_CATEGORIES.join(",");
  return {
    resourceType: "Bundle",
    type: "batch",
    entry: patientRefs.map((patientRef) => ({
      request: {
        method: "GET",
        url:
          `DocumentReference?subject=${patientRef}` +
          `&status=current` +
          `&category=${categoryParam}` +
          `&_count=100` +
          `&_sort=-_lastUpdated` +
          `&_elements=${DOC_ELEMENTS}`,
      },
    })),
  };
}

/** Batch só de MedicationDispense — isolado para não derrubar o batch de docs. */
function buildDispenseBatchBundle(patientRefs: string[]): Bundle {
  return {
    resourceType: "Bundle",
    type: "batch",
    entry: patientRefs.map((patientRef) => ({
      request: {
        method: "GET",
        url:
          `MedicationDispense?subject=${patientRef}` +
          `&_count=100` +
          `&_sort=-_lastUpdated` +
          `&_elements=${DISPENSE_ELEMENTS}`,
      },
    })),
  };
}

function mergeSearchsetIntoMaps(
  searchset: Bundle | undefined,
  documentsByPatient: Map<string, DocumentReference[]>,
  dispensesByPatient: Map<string, MedicationDispense[]>,
  organizationId: string,
): void {
  if (!searchset?.entry) return;

  for (const item of searchset.entry) {
    const resource = item.resource;
    if (!resource) continue;

    if (resource.resourceType === "DocumentReference") {
      const key = patientKeyFromReference(
        (resource as DocumentReference).subject?.reference,
      );
      if (!key) continue;
      const list = documentsByPatient.get(key) ?? [];
      list.push(resource as DocumentReference);
      documentsByPatient.set(key, list);
      continue;
    }

    if (resource.resourceType === "MedicationDispense") {
      const dispense = resource as MedicationDispense;
      if (!matchesOrganizationPerformer(dispense, organizationId)) continue;
      const key = patientKeyFromReference(dispense.subject?.reference);
      if (!key) continue;
      const list = dispensesByPatient.get(key) ?? [];
      list.push(dispense);
      dispensesByPatient.set(key, list);
    }
  }
}

function ingestBatchResponse(
  batchResponse: Bundle,
  documentsByPatient: Map<string, DocumentReference[]>,
  dispensesByPatient: Map<string, MedicationDispense[]>,
  organizationId: string,
): void {
  for (const entry of batchResponse.entry ?? []) {
    const inner = entry.resource as Bundle | undefined;
    if (inner?.resourceType === "Bundle") {
      mergeSearchsetIntoMaps(
        inner,
        documentsByPatient,
        dispensesByPatient,
        organizationId,
      );
    }
  }
}

async function postStatisticsChunks(
  chunks: string[][],
  buildBody: (chunk: string[]) => Bundle,
  headers: Record<string, string>,
): Promise<{ anyOk: boolean; bundles: Bundle[] }> {
  const results = await Promise.allSettled(
    chunks.map(async (chunk) => {
      const response = await useAxios.post(
        `${endpoints.STATISTICS}`,
        buildBody(chunk),
        { headers, timeout: TIMEOUT_MS },
      );
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`GetEcommercePatientResources: ${response.status}`);
      }
      return response.data as Bundle;
    }),
  );

  const bundles: Bundle[] = [];
  let anyOk = false;
  for (const result of results) {
    if (result.status !== "fulfilled") {
      console.error("GetEcommercePatientResources chunk failed", result.reason);
      continue;
    }
    anyOk = true;
    bundles.push(result.value);
  }
  return { anyOk, bundles };
}

/**
 * Pré-carrega docs sempre; dispense em batch separado se a flag estiver ligada.
 * Falha do batch de dispense não zera `ok` (docs continuam válidos).
 */
export async function GetEcommercePatientResources(params: {
  patients: Patient[];
  organizationId: string;
}): Promise<EcommercePatientResourcesResult> {
  const organizationId = params.organizationId?.trim();
  const documentsByPatient = new Map<string, DocumentReference[]>();
  const dispensesByPatient = new Map<string, MedicationDispense[]>();

  if (!organizationId) {
    return { documentsByPatient, dispensesByPatient, ok: false, dispensesOk: false };
  }

  const patientRefs = [
    ...new Set(
      params.patients
        .map(subjectKeyFromPatient)
        .filter((ref): ref is string => Boolean(ref)),
    ),
  ];

  if (!patientRefs.length) {
    return {
      documentsByPatient,
      dispensesByPatient,
      ok: true,
      dispensesOk: USE_ECOMMERCE_RESOURCES_BATCH_DISPENSE,
    };
  }

  const headers = await statisticsHeaders();
  const chunks = chunkArray(patientRefs, BATCH_CHUNK_SIZE);

  const docsResult = await postStatisticsChunks(
    chunks,
    buildDocsBatchBundle,
    headers,
  );
  for (const bundle of docsResult.bundles) {
    ingestBatchResponse(
      bundle,
      documentsByPatient,
      dispensesByPatient,
      organizationId,
    );
  }

  let dispensesOk = false;
  if (USE_ECOMMERCE_RESOURCES_BATCH_DISPENSE) {
    const dispenseResult = await postStatisticsChunks(
      chunks,
      buildDispenseBatchBundle,
      headers,
    );
    dispensesOk = dispenseResult.anyOk;
    for (const bundle of dispenseResult.bundles) {
      ingestBatchResponse(
        bundle,
        documentsByPatient,
        dispensesByPatient,
        organizationId,
      );
    }
  }

  return {
    documentsByPatient,
    dispensesByPatient,
    ok: docsResult.anyOk,
    dispensesOk,
  };
}
